import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation } from 'react-query';
import { queryAPI } from '../services/api';
import { QueryResponse, ChartSuggestion, InsightResult } from '../types';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import ChatMessage from '../components/ChatMessage';
import UpgradePrompt from '../components/UpgradePrompt';
import LoadingMessages from '../components/LoadingMessages';
import {
  Send,
  Loader2,
  Shield,
  MessageSquare,
  Plus,
  Sparkles,
  Lightbulb,
  SquarePen,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { config } from '../config';
import { cn } from '../lib/utils';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  queryResponse?: {
    sql: string;
    columns: string[];
    allColumns?: string[];
    defaultVisibleColumns?: string[];
    columnAliases?: Record<string, string>;
    currencyColumns?: string[];
    results: any[];
    rowCount: number;
    totalRowCount?: number;
    isLimited?: boolean;
    explanation?: string;
    suggestedFollowUps?: string[];
    chartSuggestion?: ChartSuggestion;
    insights?: InsightResult;
  };
  isUpgradePrompt?: boolean;
  upgradeDetails?: { limit: number; phone: string };
}

interface TenantContext {
  subdomain: string;
  tenantId: string;
  userId: string;
  userEmail: string;
  theme?: 'light' | 'dark';
  locale?: string;
}

interface EmbedConfig {
  allowedOrigins: string[];
  debugMode?: boolean;
}

interface ConversationItem {
  conversationId: string;
  title?: string;
  lastActivity: string;
  messageCount: number;
}

// Parse additional allowed origins from environment variable (comma-separated)
const extraEmbedOrigins = (process.env.REACT_APP_EMBED_ALLOWED_ORIGINS || '')
  .split(',')
  .map(o => o.trim())
  .filter(Boolean);

const embedConfig: EmbedConfig = {
  allowedOrigins: [
    'https://*.octopos.com',
    'https://octopos.com',
    'http://localhost:3001', // For development
    ...extraEmbedOrigins,
  ],
  debugMode: process.env.NODE_ENV === 'development',
};

const EmbeddedQueryPage: React.FC = () => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tenantContext, setTenantContext] = useState<TenantContext | null>(null);
  const [query, setQuery] = useState('');
  const [conversationId, setConversationId] = useState<string>(uuidv4());
  const [messages, setMessages] = useState<Message[]>([]);
  const [lastUserQuery, setLastUserQuery] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const parentOrigin = useRef<string>('');
  const initProcessed = useRef(false);
  const embedTokenRef = useRef<string>('');
  const isAuthorizedRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const isValidOrigin = (origin: string): boolean => {
    return embedConfig.allowedOrigins.some(allowed => {
      if (allowed.includes('*')) {
        const regex = new RegExp(allowed.replace('*', '.*'));
        return regex.test(origin);
      }
      return allowed === origin;
    });
  };

  const fetchConversations = useCallback(async () => {
    if (!embedTokenRef.current) return;
    setLoadingConversations(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/embed/conversations?limit=30`, {
        headers: { 'X-Embed-Token': embedTokenRef.current },
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.data?.conversations || []);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  const loadConversation = useCallback(async (convId: string) => {
    if (!embedTokenRef.current) return;
    setLoadingConversation(true);
    try {
      const response = await fetch(
        `${config.API_BASE_URL}/embed/conversations/${convId}/full`,
        { headers: { 'X-Embed-Token': embedTokenRef.current } }
      );
      if (response.ok) {
        const data = await response.json();
        const conv = data.data;
        if (conv && conv.messages) {
          setConversationId(conv.conversationId);
          const loadedMessages: Message[] = conv.messages.map((msg: any) => ({
            id: msg._id || uuidv4(),
            type: msg.role === 'user' ? 'user' : 'assistant',
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            queryResponse: msg.metadata?.sql ? {
              sql: msg.metadata.sql,
              columns: msg.metadata.queryResults?.columns || msg.metadata.columns || [],
              allColumns: msg.metadata.queryResults?.columns || msg.metadata.allColumns,
              defaultVisibleColumns: msg.visualization?.visibleColumns || msg.metadata.defaultVisibleColumns,
              columnAliases: msg.visualization?.columnAliases || msg.metadata.columnAliases,
              currencyColumns: msg.metadata.currencyColumns || [],
              results: msg.metadata.queryResults?.data || msg.metadata.results || [],
              rowCount: msg.metadata.queryResults?.data?.length || msg.metadata.rowCount || 0,
              totalRowCount: msg.metadata.queryResults?.totalRowCount || msg.metadata.totalRowCount || 0,
              isLimited: msg.metadata.queryResults?.isLimited || msg.metadata.isLimited || false,
              explanation: msg.metadata.explanation || msg.content,
              suggestedFollowUps: msg.metadata.suggestedFollowUps,
              chartSuggestion: msg.visualization?.chartConfig || msg.metadata.chartSuggestion,
            } : undefined,
          }));
          setMessages(loadedMessages);
          if (loadedMessages.length > 0) {
            const lastUser = [...loadedMessages].reverse().find(m => m.type === 'user');
            if (lastUser) setLastUserQuery(lastUser.content);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setLoadingConversation(false);
    }
  }, []);

  const handleMessage = async (event: MessageEvent) => {
    if (!isValidOrigin(event.origin)) {
      console.warn('Received message from unauthorized origin:', event.origin);
      return;
    }

    parentOrigin.current = event.origin;

    switch (event.data.type) {
      case 'init':
        if (initProcessed.current) return;
        initProcessed.current = true;
        try {
          const { token } = event.data.payload;
          embedTokenRef.current = token;

          // Validate token with backend
          const response = await fetch(`${config.API_BASE_URL}/embed/validate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Embed-Token': token,
            },
          });

          if (response.ok) {
            const context = await response.json();
            setTenantContext(context.data);
            setIsAuthorized(true);
            isAuthorizedRef.current = true;
            setIsLoading(false);

            // Apply theme if provided
            if (context.data?.theme) {
              document.documentElement.classList.toggle('dark', context.data.theme === 'dark');
            }

            // Send success acknowledgment
            window.parent.postMessage({
              type: 'embed-initialized',
              success: true,
            }, event.origin);
          } else {
            throw new Error('Invalid token');
          }
        } catch (err) {
          setError('Authentication failed. Please refresh and try again.');
          setIsLoading(false);

          window.parent.postMessage({
            type: 'embed-initialized',
            success: false,
            error: 'Authentication failed',
          }, event.origin);
        }
        break;

      case 'theme-change':
        if (event.data.theme) {
          document.documentElement.classList.toggle('dark', event.data.theme === 'dark');
        }
        break;

      case 'reset-conversation':
        handleNewConversation();
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    // Send ready signal
    window.parent.postMessage({ type: 'embed-ready' }, '*');

    // Timeout if no init message received
    const timeout = setTimeout(() => {
      if (!isAuthorizedRef.current) {
        setError('Connection timeout. Please refresh and try again.');
        setIsLoading(false);
      }
    }, 10000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
  }, []);

  // Fetch conversations when authorized
  useEffect(() => {
    if (isAuthorized) {
      fetchConversations();
    }
  }, [isAuthorized, fetchConversations]);

  const { mutate: executeQuery, isLoading: isQuerying } = useMutation(
    async (data: { query: string; conversationId?: string }) => {
      const hasMessages = messages.some(m => m.type === 'assistant' && m.queryResponse);

      // Include tenant context in API calls
      const headers = {
        'X-Tenant-Id': tenantContext?.tenantId || '',
        'X-Tenant-Subdomain': tenantContext?.subdomain || '',
      };

      if (hasMessages) {
        return queryAPI.continueConversation(conversationId, data.query, headers);
      } else {
        return queryAPI.processNaturalLanguage(data.query, conversationId, headers);
      }
    },
    {
      onSuccess: (data: QueryResponse) => {
        if (data.type === 'conversational') {
          setMessages(prev => [...prev, {
            id: uuidv4(),
            type: 'assistant',
            content: data.message || 'I could not generate a response.',
            timestamp: new Date(),
          }]);
          setQuery('');
          return;
        }

        setMessages(prev => [...prev, {
          id: uuidv4(),
          type: 'assistant',
          content: data.explanation || `Found ${data.rowCount} results`,
          timestamp: new Date(),
          queryResponse: {
            sql: data.query,
            columns: data.columns,
            allColumns: data.allColumns,
            defaultVisibleColumns: data.defaultVisibleColumns,
            columnAliases: data.columnAliases,
            currencyColumns: data.currencyColumns,
            results: data.results,
            rowCount: data.rowCount,
            totalRowCount: data.totalRowCount,
            isLimited: data.isLimited,
            explanation: data.explanation,
            suggestedFollowUps: data.suggestedFollowUps,
            chartSuggestion: data.chartSuggestion,
            insights: data.insights,
          }
        }]);
        setQuery('');

        // Notify parent of activity
        window.parent.postMessage({
          type: 'query-executed',
          query: lastUserQuery,
          resultCount: data.rowCount,
        }, parentOrigin.current);
      },
      onError: (error: any) => {
        const errorData = error.response?.data?.error;
        if (errorData?.code === 'CONVERSATION_LIMIT_EXCEEDED') {
          setMessages(prev => [...prev, {
            id: uuidv4(),
            type: 'assistant',
            content: '',
            timestamp: new Date(),
            isUpgradePrompt: true,
            upgradeDetails: {
              limit: errorData.details?.limit || 10,
              phone: errorData.details?.upgradePhone || '+1 (866) 658-2220',
            },
          }]);

          // Notify parent of conversation limit
          window.parent.postMessage({
            type: 'conversation-limit-exceeded',
            details: errorData.details,
          }, parentOrigin.current);
        } else {
          setMessages(prev => [...prev, {
            id: uuidv4(),
            type: 'assistant',
            content: `Error: ${error.message || 'Failed to process query'}`,
            timestamp: new Date(),
          }]);

          // Notify parent of error
          window.parent.postMessage({
            type: 'query-error',
            error: error.message,
          }, parentOrigin.current);
        }
      }
    }
  );

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (query.trim() && !isQuerying) {
      const userMessage: Message = {
        id: uuidv4(),
        type: 'user',
        content: query,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);
      setLastUserQuery(query);
      executeQuery({ query, conversationId });
    }
  };

  const handleFollowUpClick = (question: string) => {
    setQuery(question);
    setLastUserQuery(question);
    setTimeout(() => {
      const userMessage: Message = {
        id: uuidv4(),
        type: 'user',
        content: question,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);
      executeQuery({ query: question, conversationId });
      setQuery('');
    }, 100);
  };

  const handleNewConversation = () => {
    setConversationId(uuidv4());
    setMessages([]);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Initializing secure connection...</p>
        </div>
      </div>
    );
  }

  if (error || !isAuthorized) {
    return (
      <div className="flex items-center justify-center h-screen p-6">
        <Alert className="max-w-md">
          <Shield className="h-4 w-4" />
          <AlertDescription>
            {error || 'Unauthorized access. This chat interface can only be embedded in authorized applications.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Chat history sidebar */}
      <div className="border-r border-stroke bg-white flex flex-col w-[280px] flex-shrink-0">
        <div className="p-4 border-b border-stroke">
          <span className="text-sm font-semibold text-text-secondary">Chat History</span>
        </div>
        <div className="px-4 pt-4">
          <button
            onClick={handleNewConversation}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-brand-subtle text-brand hover:bg-brand-stroke transition-colors"
          >
            <SquarePen className="h-4 w-4" />
            New Chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loadingConversations ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-text-tertiary" />
            </div>
          ) : conversations.length === 0 ? (
            <p className="text-xs text-text-tertiary text-center py-4">No conversations yet</p>
          ) : (
            <div className="space-y-1">
              {conversations.map((conv) => (
                <button
                  key={conv.conversationId}
                  onClick={() => loadConversation(conv.conversationId)}
                  disabled={loadingConversation}
                  className={cn(
                    'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                    conv.conversationId === conversationId
                      ? 'bg-brand-subtle text-brand font-medium'
                      : 'hover:bg-accent'
                  )}
                >
                  <div className="flex items-start gap-2">
                    <MessageSquare className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">
                        {conv.title || 'Untitled'}
                      </p>
                      <p className="text-xs text-text-tertiary">
                        {formatDate(conv.lastActivity)} · {conv.messageCount} msgs
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Compact header for embedded mode */}
        <div className="border-b border-stroke px-4 py-2 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-600" />
              <span className="text-sm text-text-tertiary">
                Secure connection to {tenantContext?.subdomain}.octopos.com
              </span>
            </div>
            {messages.length > 0 && (
              <button
                onClick={handleNewConversation}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-brand-subtle text-brand hover:bg-brand-stroke transition-colors"
              >
                <SquarePen className="h-3.5 w-3.5" />
                New Chat
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {loadingConversation ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto px-6 pt-6">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <Sparkles className="h-16 w-16 text-brand mb-6" />
                  <h1 className="text-[28px] font-semibold text-text-secondary mb-3">
                    Meet Octo AI
                  </h1>
                  <p className="text-base text-text-tertiary max-w-lg text-center mb-8">
                    Ask questions about your data in natural language and get instant
                    insights powered by AI.
                  </p>
                  <div className="w-full max-w-lg">
                    <p className="text-xs font-medium text-text-tertiary mb-3 flex items-center gap-1.5">
                      <Lightbulb className="h-3.5 w-3.5" />
                      Suggestions
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "What were my total sales last month?",
                        "Show me top 10 customers by revenue",
                        "What products are low in stock?",
                        "Compare this year's performance to last year"
                      ].map((suggestion, i) => (
                        <button
                          key={i}
                          onClick={() => handleFollowUpClick(suggestion)}
                          className="rounded-full bg-brand-subtle text-brand text-sm px-4 py-1.5 hover:bg-brand-stroke transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      onFollowUpClick={handleFollowUpClick}
                      originalQuery={message.type === 'assistant' && message.queryResponse ? lastUserQuery : undefined}
                      conversationId={conversationId}
                      hideSQL
                    />
                  ))}
                  {isQuerying && (
                    <div className="flex gap-3 mb-6">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center">
                          <Loader2 className="w-4 h-4 text-white animate-spin" />
                        </div>
                      </div>
                      <Card className="animate-pulse">
                        <CardContent className="py-3 px-4">
                          <LoadingMessages />
                        </CardContent>
                      </Card>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
          )}

          <div className="px-6 pb-4 pt-2">
            <form
              onSubmit={handleSubmit}
              className="flex items-end gap-2 rounded-xl p-3"
              style={{
                boxShadow:
                  '0px 1px 2px rgba(27,32,41,0.12), 0px 0px 0px 1px rgba(27,32,41,0.08)',
              }}
            >
              <Textarea
                ref={textareaRef}
                placeholder="Ask a question about your data..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isQuerying}
                className="min-h-[40px] max-h-[200px] resize-none border-0 shadow-none focus-visible:ring-0 p-0"
                rows={1}
              />
              <button
                type="submit"
                disabled={isQuerying || !query.trim()}
                className={cn(
                  'flex-shrink-0 rounded-full w-9 h-9 flex items-center justify-center transition-colors',
                  query.trim() && !isQuerying
                    ? 'bg-brand text-white'
                    : 'bg-disabled-bg text-text-tertiary'
                )}
              >
                {isQuerying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </form>
            <p className="text-xs text-text-tertiary text-center mt-2">
              Answers may contain inaccurate data
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedQueryPage;
